import {
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Box, Button, makeStyles, useTheme } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity, EnumTipoSujeto } from '../../../../api'
import { AnnexedParamsModel } from '../../../../api/Argentia/endpoints/annexedParams/model/annexedParams'
import { ProductSelectOption } from '../../../../api/Argentia/endpoints/contracts/model/contracts'
import { EnumTipoLead } from '../../../../api/oData/enums/enumTipoLead'
import { EnumTipoParamAnexo } from '../../../../api/oData/enums/enumTipoParamAnexo'
import { CardWithTransition } from '../../../../components/shared/cardWithTransition/CardWithTransition'
import { enumToSelectOptionArray } from '../../../../helpers'
import { getCustomerDomain } from '../../../../redux'
import { getAnnexedParamsByType } from '../../../../services/annexedParams.service'
import { fetchTolls } from '../../../contracts/contracts.service'
import { fetchProductsBySubjectType } from '../../../products/products.service'
import { mapTollsResponseToSelectOption } from '../../leads.mapper'
import { searchFilterCardStyle } from './searchFilterCard.styles'

interface SearchFilterCardProps {
  showCard: boolean
  filters: LeadFormSearchFilter
  onSearch: (formState: LeadFormSearchFilter) => void
  onCancel: () => void
  onCleanFilters: () => void
  commodity: EnumCommodity
  leadType: EnumTipoLead
}

export interface LeadFormSearchFilter {
  code?: string
  commodity: EnumCommodity
  leadType: EnumTipoLead
  description?: string
  productId?: number[]
  tollTypeId?: number[]
}

export const SearchFilterCard: React.FC<SearchFilterCardProps> = ({
  showCard,
  filters,
  onSearch,
  onCancel,
  onCleanFilters,
  commodity,
  leadType
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'leads', 'enums'])
  const history = useHistory()
  const currentTheme = useTheme()
  const useStyles = makeStyles(searchFilterCardStyle(currentTheme))
  const classes = useStyles()
  const { info, error } = useNotify()

  const initialFormState: LeadFormSearchFilter = {
    ...filters,
    commodity: commodity,
    leadType: leadType
  }

  const [searchFormData, setSearchFormData] = useState<LeadFormSearchFilter>(initialFormState)
  const [productOptions, setProductOptions] = useState<ProductSelectOption[]>([])
  const [tollOptions, setTollOptions] = useState<SelectOption[]>([])
  const [annexedParamsOptions, setAnnexedParamsOptions] = useState<AnnexedParamsModel[]>([])

  const [minCharError, setMinCharError] = useState<boolean>(false)
  const [isFetchingProducts, setIsFetchingProducts] = useState<boolean>(false)
  const [isFetchingTolls, setIsFetchingTolls] = useState<boolean>(false)
  const [isFetchingAnnexedParams, setIsFetchingAnnexedParams] = useState<boolean>(false)

  // #region Handlers Starts
  // #endregion Handlers Ends

  // #region Functions Starts
  const handleCharValidation = (value: string) => {
    if (value != '' && value.length < 3) {
      setMinCharError(true)
    } else {
      setMinCharError(false)
    }
  }

  // #endregion Functions Ends

  // #region Form Definition Starts
  const searchFormOptions: FormOptions = {
    elevation: 0,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('leads:forms.add.analytics')
    }
  }

  const handleInputChange = (fieldName: string, value: any) => {
    setSearchFormData(prevState => ({
      ...prevState,
      [fieldName]: value
    }))
  }

  const searchFormSections: Section[] = [
    {
      title: t('leads:forms.add.section1Title'),
      inputs: [
        {
          name: 'code',
          label: t('leads:forms.add.inputs.code'),
          type: DataType.TEXT,
          helpText: t('leads:components.searchFilterCard.forms.helpText.allowedCharsCode'),
          onChange: (value, formState) => {
            handleCharValidation(value.toString())
            handleInputChange('code', value)
          }
        },
        {
          name: 'description',
          label: t('leads:forms.add.inputs.description'),
          type: DataType.TEXT,
          helpText: t('leads:components.searchFilterCard.forms.helpText.allowedCharsDescription'),
          onChange: (value, formState) => {
            handleCharValidation(value.toString())
            handleInputChange('description', value)
          }
        },
        {
          name: 'leadType',
          label: t('leads:forms.add.inputs.leadType'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumTipoLead, 'enums:EnumTipoLead'),
          readOnly: true
        },
        {
          name: 'commodity',
          label: t('leads:forms.add.inputs.commodity'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumCommodity, 'enums:EnumCommodity'),
          readOnly: true
        }
      ]
    },
    {
      title: t('leads:forms.add.section2Title'),
      inputs: [
        {
          name: 'productId',
          label: t('leads:forms.add.inputs.product'),
          isLoading: isFetchingProducts,
          type: DataType.MULTISELECT,
          selectOptions: productOptions,
          onChange: value => handleInputChange('productId', value)
        },
        {
          name: 'tollTypeId',
          label: t('leads:forms.add.inputs.toll'),
          isLoading: isFetchingTolls,
          type: DataType.MULTISELECT,
          selectOptions: mapTollsResponseToSelectOption(tollOptions),
          onChange: value => handleInputChange('tollTypeId', value)
        }
      ]
    }
  ]
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts

  useEffect(() => {
    setSearchFormData(prevState => ({
      ...prevState,
      ...filters,
      commodity: commodity,
      leadType: leadType
    }))
  }, [commodity, leadType, filters])

  useEffect(() => {
    const getTollOptions = async () => {
      setIsFetchingTolls(true)
      await fetchTolls(commodity!)
        .then(response => {
          if (response.success && response.data) {
            setTollOptions(response.data)
          } else {
            error(t('leads:fetchErrors.tolls'))
          }
        })
        .finally(() => {
          setIsFetchingTolls(false)
        })
    }
    const getProductOptions = async () => {
      setIsFetchingProducts(true)
      await fetchProductsBySubjectType(EnumTipoSujeto.ClienteFinal, commodity)
        .then(response => {
          if (response.success && response.data) {
            setProductOptions(response.data)
          } else {
            error(t('leads:fetchErrors.products'))
          }
        })
        .finally(() => {
          setIsFetchingProducts(false)
        })
    }
    const fetchAnnexedParamsByType = async () => {
      setIsFetchingAnnexedParams(true)
      await getAnnexedParamsByType(EnumTipoParamAnexo.Lead, commodity)
        .then(response => {
          if (response.success && response.data) {
            setAnnexedParamsOptions(response.data)
          } else {
            error(t('leads:fetchErrors.annexedParams'))
          }
        })
        .finally(() => {
          setIsFetchingAnnexedParams(false)
        })
    }

    if (showCard) {
      fetchAnnexedParamsByType()
      getTollOptions()
      getProductOptions()
    }
  }, [showCard, commodity])

  // #endregion Hooks Ends

  return (
    <CardWithTransition show={showCard} unmountOnExit={true} className={classes.card}>
      <Form
        title={t('leads:components.searchFilterCard.forms.title')}
        sections={searchFormSections}
        data={searchFormData}
        options={searchFormOptions}
      />
      <Box className={classes.footer}>
        <Button variant="text" color="secondary" size="small" onClick={onCancel}>
          {t('leads:components.searchFilterCard.actions.cancel')}
        </Button>
        <Button variant="text" color="primary" size="small" onClick={onCleanFilters}>
          {t('leads:components.searchFilterCard.actions.clean')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={minCharError}
          onClick={() => {
            onSearch(searchFormData)
          }}
        >
          {t('leads:components.searchFilterCard.actions.search')}
        </Button>
      </Box>
    </CardWithTransition>
  )
}
